import { ImageTextChange, SlideChange } from '@/client/courses';
import { InlineEditableField } from '@/components/courses/InlineEditableField';
import {
  HighlightedHtml,
  HighlightedText,
} from '@/components/text-highlighting';
import { extractTextFromHtml } from '@/components/text-highlighting/utils';
import { RichTextRenderer } from '@/ui/rich-text-renderer/RichTextRenderer';
import { SlideDeckIcon } from '@/utils/helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIconButton } from './ActionIconButton';
import {
  ChevronIcon,
  ComparisonBox,
  ComparisonContent,
  ComparisonLabel,
  ComparisonRow,
  ContentHeader,
  ContentHeaderLeft,
  ContentIcon,
  ContentSection,
  ContentTitle,
  HeaderButtonContainer,
  LabelText,
  ModificationBadge,
  SlideHeader,
  SlideHeaderLeft,
  SlideItemContainer,
  SlideTitle,
} from './course-changes.styles';
import { PendingSlideEdit } from './course-changes.types';
import { countSlideChanges } from './course-changes.utils';

export type SlideChangesDisplayProps = {
  slideChanges: SlideChange[];
  contentTitle: string;
  modificationCount: number;
  className?: string;
  isFirst?: boolean;
  contentIndex: number;
  onSlideEdit: (edit: PendingSlideEdit) => void;
  pendingEdits: PendingSlideEdit[];
};

export const SlideChangesDisplay: React.FC<SlideChangesDisplayProps> = ({
  slideChanges,
  contentTitle,
  modificationCount,
  className,
  isFirst = false,
  contentIndex,
  onSlideEdit,
  pendingEdits,
}) => {
  const { t } = useTranslation();
  const [expandedContent, setExpandedContent] = useState(isFirst);
  const [expandedSlides, setExpandedSlides] = useState<Set<number>>(
    isFirst && slideChanges.length > 0 ? new Set([0]) : new Set(),
  );
  const [editingField, setEditingField] = useState<{
    slideIndex: number;
    field:
      | 'headline'
      | 'content'
      | 'footnote'
      | 'infobox'
      | 'imageTitle'
      | 'imageDescription'
      | 'ieSender'
      | 'ieMessage'
      | 'ieHeader'
      | 'ieBody';
    imageIndex?: number;
  } | null>(null);
  const [editHandlers, setEditHandlers] = useState<{
    onSave: () => void;
    onCancel: () => void;
  } | null>(null);

  const toggleSlide = (index: number) => {
    const newExpanded = new Set(expandedSlides);

    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }

    setExpandedSlides(newExpanded);
  };

  const handleCancelEdit = () => {
    if (!editHandlers) return;

    editHandlers.onCancel();
    setEditingField(null);
    setEditHandlers(null);
  };

  const handleConfirmEdit = () => {
    if (!editHandlers) return;

    editHandlers.onSave();
    setEditingField(null);
    setEditHandlers(null);
  };

  return (
    <ContentSection className={className}>
      <ContentHeader onClick={() => setExpandedContent(!expandedContent)}>
        <ContentHeaderLeft>
          <ContentIcon>{<SlideDeckIcon />}</ContentIcon>
          <ContentTitle>{contentTitle}</ContentTitle>
          <ModificationBadge>
            {t('course.changes.slidesModified', { count: modificationCount })}
          </ModificationBadge>
        </ContentHeaderLeft>
        <ChevronIcon className={expandedContent ? 'expanded' : ''}>
          <i className="pi pi-chevron-down" />
        </ChevronIcon>
      </ContentHeader>

      {expandedContent &&
        slideChanges.map((slide, index) => {
          const changeCount = countSlideChanges(slide);
          return (
            <SlideItemContainer key={index}>
              <SlideHeader onClick={() => toggleSlide(index)}>
                <SlideHeaderLeft>
                  <SlideTitle>Slide {index + 1}</SlideTitle>
                  <ModificationBadge>
                    {changeCount === 1
                      ? t('course.changes.slideChange', {
                          count: changeCount,
                        })
                      : t('course.changes.slideChanges', {
                          count: changeCount,
                        })}
                  </ModificationBadge>
                </SlideHeaderLeft>
                <ChevronIcon
                  className={expandedSlides.has(index) ? 'expanded' : ''}
                >
                  <i className="pi pi-chevron-down" />
                </ChevronIcon>
              </SlideHeader>

              {expandedSlides.has(index) && (
                <>
                  {/* Headlines */}
                  {(slide.originalHeadline || slide.customizedHeadline) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          {t('course.changes.originalHeadline')}
                        </ComparisonLabel>
                        <ComparisonContent>
                          <RichTextRenderer
                            content={slide.originalHeadline || ''}
                          />
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>
                            {t('course.changes.customizedHeadline')}
                          </LabelText>
                          {editingField?.slideIndex === index &&
                          editingField?.field === 'headline' &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : (
                            <ActionIconButton
                              onClick={() => {
                                setEditingField({
                                  slideIndex: index,
                                  field: 'headline',
                                });
                              }}
                              aria-label={t('generic.edit')}
                              icon="edit"
                            />
                          )}
                        </ComparisonLabel>
                        <ComparisonContent>
                          <InlineEditableField
                            value={
                              pendingEdits.find(
                                (e) =>
                                  e.contentIndex === contentIndex &&
                                  e.slideIndex === index &&
                                  e.field === 'headline',
                              )?.value ??
                              slide.customizedHeadline ??
                              null
                            }
                            onSave={(value) => {
                              onSlideEdit({
                                contentIndex,
                                slideIndex: index,
                                field: 'headline',
                                value,
                              });
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            onCancel={() => {
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            isHtml={true}
                            placeholder={t(
                              'course.changes.placeholderHeadline',
                            )}
                            renderButtonsInHeader={true}
                            isEditing={
                              editingField?.slideIndex === index &&
                              editingField?.field === 'headline'
                            }
                            onHandlersReady={(handlers) => {
                              setEditHandlers({
                                onSave: () => {
                                  handlers.onSave();
                                },
                                onCancel: () => {
                                  handlers.onCancel();
                                },
                              });
                            }}
                          >
                            {slide.customizedHeadline ? (
                              <HighlightedText
                                originalText={extractTextFromHtml(
                                  slide.originalHeadline || '',
                                )}
                                newText={extractTextFromHtml(
                                  (pendingEdits.find(
                                    (e) =>
                                      e.contentIndex === contentIndex &&
                                      e.slideIndex === index &&
                                      e.field === 'headline',
                                  )?.value ??
                                    slide.customizedHeadline) ||
                                    '',
                                )}
                                style={{ fontSize: 'var(--medium-font-size)' }}
                              />
                            ) : (
                              ''
                            )}
                          </InlineEditableField>
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}

                  {/* Content */}
                  {(slide.originalContent || slide.customizedContent) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          {t('course.changes.originalContent')}
                        </ComparisonLabel>
                        <ComparisonContent className="html-content">
                          {slide.originalContent ? (
                            <RichTextRenderer content={slide.originalContent} />
                          ) : (
                            <div style={{ fontStyle: 'italic' }}>
                              {t('generic.none')}
                            </div>
                          )}
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>
                            {t('course.changes.customizedContent')}
                          </LabelText>
                          {editingField?.slideIndex === index &&
                          editingField?.field === 'content' &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : (
                            <ActionIconButton
                              onClick={() => {
                                setEditingField({
                                  slideIndex: index,
                                  field: 'content',
                                });
                              }}
                              aria-label={t('generic.edit')}
                              icon="edit"
                            />
                          )}
                        </ComparisonLabel>
                        <ComparisonContent className="html-content">
                          <InlineEditableField
                            value={
                              pendingEdits.find(
                                (e) =>
                                  e.contentIndex === contentIndex &&
                                  e.slideIndex === index &&
                                  e.field === 'content',
                              )?.value ??
                              slide.customizedContent ??
                              null
                            }
                            onSave={(value) => {
                              onSlideEdit({
                                contentIndex,
                                slideIndex: index,
                                field: 'content',
                                value,
                              });
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            onCancel={() => {
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            isHtml={true}
                            placeholder={t('course.changes.placeholderContent')}
                            renderButtonsInHeader={true}
                            isEditing={
                              editingField?.slideIndex === index &&
                              editingField?.field === 'content'
                            }
                            onHandlersReady={(handlers) => {
                              setEditHandlers({
                                onSave: () => {
                                  handlers.onSave();
                                },
                                onCancel: () => {
                                  handlers.onCancel();
                                },
                              });
                            }}
                          >
                            <HighlightedHtml
                              originalHtml={slide.originalContent || ''}
                              newHtml={
                                (pendingEdits.find(
                                  (e) =>
                                    e.contentIndex === contentIndex &&
                                    e.slideIndex === index &&
                                    e.field === 'content',
                                )?.value ??
                                  slide.customizedContent) ||
                                ''
                              }
                            />
                          </InlineEditableField>
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}

                  {/* Footnotes */}
                  {(slide.originalFootnote || slide.customizedFootnote) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          {t('course.changes.originalFootnote')}
                        </ComparisonLabel>
                        <ComparisonContent>
                          {slide.originalFootnote || t('generic.none')}
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>
                            {t('course.changes.customizedFootnote')}
                          </LabelText>
                          {editingField?.slideIndex === index &&
                          editingField?.field === 'footnote' &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : (
                            <ActionIconButton
                              onClick={() => {
                                setEditingField({
                                  slideIndex: index,
                                  field: 'footnote',
                                });
                              }}
                              aria-label={t('generic.edit')}
                              icon="edit"
                            />
                          )}
                        </ComparisonLabel>
                        <ComparisonContent>
                          <InlineEditableField
                            value={
                              pendingEdits.find(
                                (e) =>
                                  e.contentIndex === contentIndex &&
                                  e.slideIndex === index &&
                                  e.field === 'footnote',
                              )?.value ??
                              slide.customizedFootnote ??
                              null
                            }
                            onSave={(value) => {
                              onSlideEdit({
                                contentIndex,
                                slideIndex: index,
                                field: 'footnote',
                                value,
                              });
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            onCancel={() => {
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            isHtml={true}
                            placeholder={t(
                              'course.changes.placeholderFootnote',
                            )}
                            renderButtonsInHeader={true}
                            isEditing={
                              editingField?.slideIndex === index &&
                              editingField?.field === 'footnote'
                            }
                            onHandlersReady={(handlers) => {
                              setEditHandlers({
                                onSave: () => {
                                  handlers.onSave();
                                },
                                onCancel: () => {
                                  handlers.onCancel();
                                },
                              });
                            }}
                          >
                            {slide.customizedFootnote ? (
                              <HighlightedText
                                originalText={extractTextFromHtml(
                                  slide.originalFootnote || '',
                                )}
                                newText={extractTextFromHtml(
                                  (pendingEdits.find(
                                    (e) =>
                                      e.contentIndex === contentIndex &&
                                      e.slideIndex === index &&
                                      e.field === 'footnote',
                                  )?.value ??
                                    slide.customizedFootnote) ||
                                    '',
                                )}
                              />
                            ) : (
                              t('generic.none')
                            )}
                          </InlineEditableField>
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}

                  {/* Infoboxes */}
                  {(slide.originalInfobox || slide.customizedInfobox) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          {t('course.changes.originalInfobox')}
                        </ComparisonLabel>
                        <ComparisonContent>
                          {slide.originalInfobox || t('generic.none')}
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>
                            {t('course.changes.customizedInfobox')}
                          </LabelText>
                          {editingField?.slideIndex === index &&
                          editingField?.field === 'infobox' &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : (
                            <ActionIconButton
                              onClick={() => {
                                setEditingField({
                                  slideIndex: index,
                                  field: 'infobox',
                                });
                              }}
                              aria-label={t('generic.edit')}
                              icon="edit"
                            />
                          )}
                        </ComparisonLabel>
                        <ComparisonContent>
                          <InlineEditableField
                            value={
                              pendingEdits.find(
                                (e) =>
                                  e.contentIndex === contentIndex &&
                                  e.slideIndex === index &&
                                  e.field === 'infobox',
                              )?.value ??
                              slide.customizedInfobox ??
                              null
                            }
                            onSave={(value) => {
                              onSlideEdit({
                                contentIndex,
                                slideIndex: index,
                                field: 'infobox',
                                value,
                              });
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            onCancel={() => {
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            isHtml={true}
                            placeholder={t('course.changes.placeholderInfobox')}
                            renderButtonsInHeader={true}
                            isEditing={
                              editingField?.slideIndex === index &&
                              editingField?.field === 'infobox'
                            }
                            onHandlersReady={(handlers) => {
                              setEditHandlers({
                                onSave: () => {
                                  handlers.onSave();
                                },
                                onCancel: () => {
                                  handlers.onCancel();
                                },
                              });
                            }}
                          >
                            {slide.customizedInfobox ? (
                              <HighlightedText
                                originalText={extractTextFromHtml(
                                  slide.originalInfobox || '',
                                )}
                                newText={extractTextFromHtml(
                                  (pendingEdits.find(
                                    (e) =>
                                      e.contentIndex === contentIndex &&
                                      e.slideIndex === index &&
                                      e.field === 'infobox',
                                  )?.value ??
                                    slide.customizedInfobox) ||
                                    '',
                                )}
                              />
                            ) : (
                              t('generic.none')
                            )}
                          </InlineEditableField>
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}

                  {/* Interactive element (SMS/email) */}
                  {(() => {
                    const ie = slide.interactiveElementChange;

                    if (!ie) return null;

                    const isSms = ie.type === 'sms';
                    type IeField =
                      | 'ieSender'
                      | 'ieMessage'
                      | 'ieHeader'
                      | 'ieBody';
                    const ieFields: {
                      field: IeField;
                      original: string | null | undefined;
                      customized: string | null | undefined;
                      originalKey: string;
                      customizedKey: string;
                      placeholder: string;
                      show: boolean;
                      isHtml: boolean;
                    }[] = [
                      {
                        field: 'ieSender',
                        original: ie.originalSender,
                        customized: ie.customizedSender,
                        originalKey: 'course.changes.originalInteractiveSender',
                        customizedKey:
                          'course.changes.customizedInteractiveSender',
                        placeholder: t('course.changes.placeholderSender'),
                        show: !!(ie.originalSender || ie.customizedSender),
                        isHtml: false,
                      },
                      {
                        field: 'ieMessage',
                        original: ie.originalMessage,
                        customized: ie.customizedMessage,
                        originalKey:
                          'course.changes.originalInteractiveMessage',
                        customizedKey:
                          'course.changes.customizedInteractiveMessage',
                        placeholder: t('course.changes.placeholderMessage'),
                        show:
                          isSms &&
                          !!(ie.originalMessage || ie.customizedMessage),
                        isHtml: true,
                      },
                      {
                        field: 'ieHeader',
                        original: ie.originalHeader,
                        customized: ie.customizedHeader,
                        originalKey: 'course.changes.originalInteractiveHeader',
                        customizedKey:
                          'course.changes.customizedInteractiveHeader',
                        placeholder: t('course.changes.placeholderEmailHeader'),
                        show:
                          !isSms &&
                          !!(ie.originalHeader || ie.customizedHeader),
                        isHtml: true,
                      },
                      {
                        field: 'ieBody',
                        original: ie.originalBody,
                        customized: ie.customizedBody,
                        originalKey: 'course.changes.originalInteractiveBody',
                        customizedKey:
                          'course.changes.customizedInteractiveBody',
                        placeholder: t('course.changes.placeholderEmailBody'),
                        show:
                          !isSms && !!(ie.originalBody || ie.customizedBody),
                        isHtml: true,
                      },
                    ];
                    return ieFields
                      .filter((f) => f.show)
                      .map(
                        ({
                          field,
                          original,
                          customized,
                          originalKey,
                          customizedKey,
                          placeholder,
                          isHtml: fieldIsHtml,
                        }) => (
                          <ComparisonRow key={field}>
                            <ComparisonBox type="original">
                              <ComparisonLabel type="original">
                                {t(originalKey)}
                              </ComparisonLabel>
                              <ComparisonContent
                                className={
                                  fieldIsHtml ? 'html-content' : undefined
                                }
                              >
                                {original ? (
                                  fieldIsHtml ? (
                                    <RichTextRenderer content={original} />
                                  ) : (
                                    original
                                  )
                                ) : (
                                  t('generic.none')
                                )}
                              </ComparisonContent>
                            </ComparisonBox>
                            <ComparisonBox type="customized">
                              <ComparisonLabel type="customized">
                                <LabelText>{t(customizedKey)}</LabelText>
                                {editingField?.slideIndex === index &&
                                editingField?.field === field &&
                                editHandlers ? (
                                  <HeaderButtonContainer>
                                    <ActionIconButton
                                      onClick={handleCancelEdit}
                                      aria-label={t('generic.cancelEdit')}
                                      icon="cancel"
                                    />
                                    <ActionIconButton
                                      onClick={handleConfirmEdit}
                                      aria-label={t('generic.confirmEdit')}
                                      icon="confirm"
                                    />
                                  </HeaderButtonContainer>
                                ) : (
                                  <ActionIconButton
                                    onClick={() =>
                                      setEditingField({
                                        slideIndex: index,
                                        field,
                                      })
                                    }
                                    aria-label={t('generic.edit')}
                                    icon="edit"
                                  />
                                )}
                              </ComparisonLabel>
                              <ComparisonContent
                                className={
                                  fieldIsHtml ? 'html-content' : undefined
                                }
                              >
                                <InlineEditableField
                                  value={
                                    pendingEdits.find(
                                      (e) =>
                                        e.contentIndex === contentIndex &&
                                        e.slideIndex === index &&
                                        e.field === field,
                                    )?.value ??
                                    customized ??
                                    null
                                  }
                                  onSave={(value) => {
                                    onSlideEdit({
                                      contentIndex,
                                      slideIndex: index,
                                      field,
                                      value,
                                    });
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  onCancel={() => {
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  isHtml={fieldIsHtml}
                                  placeholder={placeholder}
                                  renderButtonsInHeader={true}
                                  isEditing={
                                    editingField?.slideIndex === index &&
                                    editingField?.field === field
                                  }
                                  onHandlersReady={(handlers) => {
                                    setEditHandlers({
                                      onSave: () => handlers.onSave(),
                                      onCancel: () => handlers.onCancel(),
                                    });
                                  }}
                                >
                                  {customized ? (
                                    fieldIsHtml ? (
                                      <HighlightedHtml
                                        originalHtml={original || ''}
                                        newHtml={
                                          pendingEdits.find(
                                            (e) =>
                                              e.contentIndex === contentIndex &&
                                              e.slideIndex === index &&
                                              e.field === field,
                                          )?.value ?? customized
                                        }
                                      />
                                    ) : (
                                      <HighlightedText
                                        originalText={original || ''}
                                        newText={
                                          pendingEdits.find(
                                            (e) =>
                                              e.contentIndex === contentIndex &&
                                              e.slideIndex === index &&
                                              e.field === field,
                                          )?.value ?? customized
                                        }
                                      />
                                    )
                                  ) : (
                                    t('generic.none')
                                  )}
                                </InlineEditableField>
                              </ComparisonContent>
                            </ComparisonBox>
                          </ComparisonRow>
                        ),
                      );
                  })()}

                  {/* Image texts */}
                  {slide.imageTextChanges?.map(
                    (imgChange: ImageTextChange, imgIndex: number) => (
                      <React.Fragment key={imgIndex}>
                        {(imgChange.originalTitle ||
                          imgChange.customizedTitle) && (
                          <ComparisonRow>
                            <ComparisonBox type="original">
                              <ComparisonLabel type="original">
                                {t('course.changes.originalImageTitle', {
                                  position: imgChange.position + 1,
                                })}
                              </ComparisonLabel>
                              <ComparisonContent>
                                {imgChange.originalTitle || t('generic.none')}
                              </ComparisonContent>
                            </ComparisonBox>
                            <ComparisonBox type="customized">
                              <ComparisonLabel type="customized">
                                <LabelText>
                                  {t('course.changes.customizedImageTitle', {
                                    position: imgChange.position + 1,
                                  })}
                                </LabelText>
                                {editingField?.slideIndex === index &&
                                editingField?.field === 'imageTitle' &&
                                editingField?.imageIndex === imgIndex &&
                                editHandlers ? (
                                  <HeaderButtonContainer>
                                    <ActionIconButton
                                      onClick={handleCancelEdit}
                                      aria-label={t('generic.cancelEdit')}
                                      icon="cancel"
                                    />
                                    <ActionIconButton
                                      onClick={handleConfirmEdit}
                                      aria-label={t('generic.confirmEdit')}
                                      icon="confirm"
                                    />
                                  </HeaderButtonContainer>
                                ) : (
                                  <ActionIconButton
                                    onClick={() =>
                                      setEditingField({
                                        slideIndex: index,
                                        field: 'imageTitle',
                                        imageIndex: imgIndex,
                                      })
                                    }
                                    aria-label={t('generic.edit')}
                                    icon="edit"
                                  />
                                )}
                              </ComparisonLabel>
                              <ComparisonContent>
                                <InlineEditableField
                                  value={
                                    pendingEdits.find(
                                      (e) =>
                                        e.contentIndex === contentIndex &&
                                        e.slideIndex === index &&
                                        e.field === 'imageTitle' &&
                                        e.imageIndex === imgIndex,
                                    )?.value ??
                                    imgChange.customizedTitle ??
                                    null
                                  }
                                  onSave={(value) => {
                                    onSlideEdit({
                                      contentIndex,
                                      slideIndex: index,
                                      field: 'imageTitle',
                                      value,
                                      imageIndex: imgIndex,
                                    });
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  onCancel={() => {
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  isHtml={false}
                                  placeholder={t(
                                    'course.changes.placeholderImageTitle',
                                  )}
                                  renderButtonsInHeader={true}
                                  isEditing={
                                    editingField?.slideIndex === index &&
                                    editingField?.field === 'imageTitle' &&
                                    editingField?.imageIndex === imgIndex
                                  }
                                  onHandlersReady={(handlers) => {
                                    setEditHandlers({
                                      onSave: () => handlers.onSave(),
                                      onCancel: () => handlers.onCancel(),
                                    });
                                  }}
                                >
                                  {imgChange.customizedTitle ? (
                                    <HighlightedText
                                      originalText={
                                        imgChange.originalTitle || ''
                                      }
                                      newText={
                                        pendingEdits.find(
                                          (e) =>
                                            e.contentIndex === contentIndex &&
                                            e.slideIndex === index &&
                                            e.field === 'imageTitle' &&
                                            e.imageIndex === imgIndex,
                                        )?.value ?? imgChange.customizedTitle
                                      }
                                    />
                                  ) : (
                                    t('generic.none')
                                  )}
                                </InlineEditableField>
                              </ComparisonContent>
                            </ComparisonBox>
                          </ComparisonRow>
                        )}
                        {(imgChange.originalDescription ||
                          imgChange.customizedDescription) && (
                          <ComparisonRow>
                            <ComparisonBox type="original">
                              <ComparisonLabel type="original">
                                {t('course.changes.originalImageDescription', {
                                  position: imgChange.position + 1,
                                })}
                              </ComparisonLabel>
                              <ComparisonContent>
                                {imgChange.originalDescription ||
                                  t('generic.none')}
                              </ComparisonContent>
                            </ComparisonBox>
                            <ComparisonBox type="customized">
                              <ComparisonLabel type="customized">
                                <LabelText>
                                  {t(
                                    'course.changes.customizedImageDescription',
                                    { position: imgChange.position + 1 },
                                  )}
                                </LabelText>
                                {editingField?.slideIndex === index &&
                                editingField?.field === 'imageDescription' &&
                                editingField?.imageIndex === imgIndex &&
                                editHandlers ? (
                                  <HeaderButtonContainer>
                                    <ActionIconButton
                                      onClick={handleCancelEdit}
                                      aria-label={t('generic.cancelEdit')}
                                      icon="cancel"
                                    />
                                    <ActionIconButton
                                      onClick={handleConfirmEdit}
                                      aria-label={t('generic.confirmEdit')}
                                      icon="confirm"
                                    />
                                  </HeaderButtonContainer>
                                ) : (
                                  <ActionIconButton
                                    onClick={() =>
                                      setEditingField({
                                        slideIndex: index,
                                        field: 'imageDescription',
                                        imageIndex: imgIndex,
                                      })
                                    }
                                    aria-label={t('generic.edit')}
                                    icon="edit"
                                  />
                                )}
                              </ComparisonLabel>
                              <ComparisonContent>
                                <InlineEditableField
                                  value={
                                    pendingEdits.find(
                                      (e) =>
                                        e.contentIndex === contentIndex &&
                                        e.slideIndex === index &&
                                        e.field === 'imageDescription' &&
                                        e.imageIndex === imgIndex,
                                    )?.value ??
                                    imgChange.customizedDescription ??
                                    null
                                  }
                                  onSave={(value) => {
                                    onSlideEdit({
                                      contentIndex,
                                      slideIndex: index,
                                      field: 'imageDescription',
                                      value,
                                      imageIndex: imgIndex,
                                    });
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  onCancel={() => {
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  isHtml={false}
                                  placeholder={t(
                                    'course.changes.placeholderImageDescription',
                                  )}
                                  renderButtonsInHeader={true}
                                  isEditing={
                                    editingField?.slideIndex === index &&
                                    editingField?.field ===
                                      'imageDescription' &&
                                    editingField?.imageIndex === imgIndex
                                  }
                                  onHandlersReady={(handlers) => {
                                    setEditHandlers({
                                      onSave: () => handlers.onSave(),
                                      onCancel: () => handlers.onCancel(),
                                    });
                                  }}
                                >
                                  {imgChange.customizedDescription ? (
                                    <HighlightedText
                                      originalText={
                                        imgChange.originalDescription || ''
                                      }
                                      newText={
                                        pendingEdits.find(
                                          (e) =>
                                            e.contentIndex === contentIndex &&
                                            e.slideIndex === index &&
                                            e.field === 'imageDescription' &&
                                            e.imageIndex === imgIndex,
                                        )?.value ??
                                        imgChange.customizedDescription
                                      }
                                    />
                                  ) : (
                                    t('generic.none')
                                  )}
                                </InlineEditableField>
                              </ComparisonContent>
                            </ComparisonBox>
                          </ComparisonRow>
                        )}
                      </React.Fragment>
                    ),
                  )}
                </>
              )}
            </SlideItemContainer>
          );
        })}
    </ContentSection>
  );
};
